/*! *********************************************************************
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import React from 'react';
import {
  useRefresh,
  useInput,
  ImageField,
  TextField,
  SimpleShowLayout,
} from 'react-admin';
import _get from 'lodash/get';
import _last from 'lodash/last';
import _forEach from 'lodash/forEach';
import _toInteger from 'lodash/toInteger';

import Grid from '@material-ui/core/Grid';
import FormHelperText from '@material-ui/core/FormHelperText';
import AttachPreviewButton from './AttachPreviewButton';
import EditPreviewButton from './EditPreviewButton';
import UpdateThumbnailButton from './UpdateThumbnailButton';
import DownloadPreviewsButton from './DownloadPreviewsButton';
import DeletePreviewButton from './DeletePreviewButton';
import SimpleDatagrid from './SimpleDatagrid';
import styled from 'styled-components';

import {
  SET_THUMBNAIL,
  ADD_PREVIEW_ATTACHMENT,
  DELETE_ATTACHMENT,
  ADD_BACKPLATE,
  DELETE_BACKPLATE,
  REPLACE_BACKPLATE,
  UPDATE_ATTACHMENT,
  ORDER_ATTACHMENT,
  REPLACE_ATTACHMENT,
} from '../graphql/attachments';
import uploadFile from '../services/UploadFile';
import auth from '../providers/auth-service';
import { mutateGraphQL } from '../helpers/dataHelpers';
import {
  getPreviews,
  PREVIEW_TYPES,
  PREVIEW_ATTACHMENTS_TYPE,
  ASSET_TYPE_PREVIEW_TYPES,
} from '../models/asset';

export default function (props) {
  const {
    input: { onChange },
    meta: { error },
  } = useInput({ ...props, name: 'previews' });
  const refresh = useRefresh();

  async function handlePreviewAdded(preview) {
    const file = _get(preview, 'file.rawFile', null);
    const isThumbnail = preview.type === PREVIEW_TYPES.THUMBNAIL;
    const isBackplate = preview.type === PREVIEW_TYPES.BACKPLATE;

    let mutate = ADD_PREVIEW_ATTACHMENT;
    if (isThumbnail) mutate = SET_THUMBNAIL;
    if (isBackplate) mutate = ADD_BACKPLATE;

    const accessToken = await auth.getAccessToken();
    const uploadedFile = await uploadFile(file, accessToken);

    const updatedAsset = await mutateGraphQL(mutate, {
      ...(!isThumbnail && { label: preview.label }),
      fileId: _get(uploadedFile, 'body.id'),
      assetId: props.assetId,
      extraTags: PREVIEW_ATTACHMENTS_TYPE[preview.type].tags,
    });

    const previews = getPreviews(
      _get(updatedAsset, 'addPreviewAttachment.attachments')
    );
    onChange(previews);
    refresh();
  }

  async function handleEditThumbnail(thumbnail) {
    const file = _get(thumbnail, 'file.rawFile', null);

    if (!file) return;

    const accessToken = await auth.getAccessToken();
    const uploadedFile = await uploadFile(file, accessToken);

    await mutateGraphQL(SET_THUMBNAIL, {
      fileId: _get(uploadedFile, 'body.id'),
      assetId: props.assetId,
    });

    refresh();
  }

  async function handePreviewRemoved(record) {
    const attachment = props.record.previews[record.index];
    const isBackplate = attachment.type === PREVIEW_TYPES.BACKPLATE;
    const mutate = isBackplate ? DELETE_BACKPLATE : DELETE_ATTACHMENT;

    await mutateGraphQL(mutate, {
      attachmentId: attachment.id,
      downloadId: attachment.id,
      assetId: props.assetId,
    });

    refresh();
  }

  async function handlePreviewEdited(record) {
    const file = _get(record, 'file.rawFile', null);
    const recordWeight = _toInteger(_get(record, 'weight', null));
    const attachment = props.record.previews[record.index];
    const isBackplate = attachment.type === PREVIEW_TYPES.BACKPLATE;
    const mutate = isBackplate ? REPLACE_BACKPLATE : REPLACE_ATTACHMENT;
    let updatedPreviews = props.record.previews;

    let variables = {
      attachmentId: attachment.id,
      downloadId: attachment.id,
      assetId: props.assetId,
      label: record.label,
      extraTags: PREVIEW_ATTACHMENTS_TYPE[record.type].tags,
    };

    if (file) {
      // replace attachment
      const accessToken = await auth.getAccessToken();
      const uploadedFile = await uploadFile(file, accessToken);
      variables.fileId = _get(uploadedFile, 'body.id');

      const updatedAsset = await mutateGraphQL(mutate, variables);
      updatedPreviews = getPreviews(
        _get(updatedAsset, 'data.addPreviewAttachment.attachments')
      );
      onChange(updatedPreviews);

      // update the id of the current record after the replacement
      record.id = _last(updatedPreviews).id;
    } else {
      // update attachment
      variables.update = {
        label: variables.label,
        extraTags: variables.extraTags,
      };
      delete variables.label;
      delete variables.extraTags;
      await mutateGraphQL(UPDATE_ATTACHMENT, variables);
    }

    // order attachment
    let attachmentIds = [];
    let attachmentPositionChanged = false;
    _forEach(updatedPreviews, (p, index) => {
      if (p.id === record.id) {
        attachmentPositionChanged = index + 1 !== recordWeight;
      }
      if (p.id !== record.id) {
        attachmentIds.push(p.id);
      }
    });

    if (attachmentPositionChanged) {
      attachmentIds.splice(recordWeight, 0, record.id);
      await mutateGraphQL(ORDER_ATTACHMENT, {
        attachmentIds: attachmentIds,
        assetId: props.assetId,
      });
    }

    refresh();
  }

  function getDatas(val) {
    let ids = [];
    let data = {};

    _forEach(val, (p, index) => {
      p.index = index;
      p.weight = index + 1;
      data[index] = p;
      ids.push(index);
    });

    return { data, ids };
  }

  function withPreviews() {
    const previewTypes = _get(ASSET_TYPE_PREVIEW_TYPES, props.assetType, []);
    return previewTypes.length > 0;
  }

  return (
    <React.Fragment>
      <SimpleShowLayout>
        <ImageField label="Thumbnail" source="thumbnail.url" />
        {props.record.modelPreview && (
          <a
            href={`https://rte-web-viewer.substance3d.com/model/${encodeURIComponent(
              props.record.modelPreview.url
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Preview 3D model
          </a>
        )}
      </SimpleShowLayout>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item>
          <UpdateThumbnailButton
            {...props}
            source="thumbnail"
            onChange={handleEditThumbnail}
          />
        </Grid>
        {withPreviews() && (
          <Grid item>
            <AttachPreviewButton
              {...props}
              record={{}}
              onPreviewAdded={handlePreviewAdded}
            />
          </Grid>
        )}
        <Grid item>
          <DownloadPreviewsButton {...props} />
        </Grid>
      </Grid>

      {error?.previews && (
        <FormHelperText error={true}>{error.previews}</FormHelperText>
      )}
      {props.record.previews?.length > 0 && (
        <SimpleDatagrid {...props} {...getDatas(props.record.previews)}>
          <TextField source="weight" label="Position" sortable={false} />
          <PreviewImage
            label="Previews"
            source="url"
            title="label"
            sortable={false}
          />
          <TextField source="label" label="Label" sortable={false} />

          <EditPreviewButton
            {...props}
            onChange={handlePreviewEdited}
            label="Edit"
          />
          <DeletePreviewButton
            previewRemoved={handePreviewRemoved}
            label="Remove"
          />
        </SimpleDatagrid>
      )}
    </React.Fragment>
  );
}

const PreviewImage = styled(ImageField)`
  text-align: center;
`;
